import api from './api';

export type MembershipRole =
  | 'OWNER'
  | 'ADMIN'
  | 'OPERATIONS_MANAGER'
  | 'DISPATCHER'
  | 'APPROVER'
  | 'ACCOUNTING'
  | 'DRIVER'
  | 'VIEWER';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
  phone?: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status?: string;
  emailVerified?: boolean;
  createdAt: string;
  updatedAt: string;
  activeCompanyId?: string | null;
  membershipRole?: MembershipRole | null;
}

export interface AuthMembership {
  id: string;
  companyId: string;
  role: MembershipRole;
  companyName: string;
}

export interface TenantAuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  memberships: AuthMembership[];
  activeCompanyId: string;
  membershipRole?: MembershipRole;
}

export interface RegisterResponse extends TenantAuthResponse {
  message: string;
}

export interface ProfileResponse extends User {
  memberships: AuthMembership[];
}

type AuthSyncListener = (response: TenantAuthResponse) => void;
const authSyncListeners = new Set<AuthSyncListener>();

export function onAuthSync(listener: AuthSyncListener): () => void {
  authSyncListeners.add(listener);
  return () => authSyncListeners.delete(listener);
}

function notifyAuthSync(response: TenantAuthResponse): void {
  authSyncListeners.forEach((listener) => listener(response));
}

function storeTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem('accessToken', accessToken);
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
}

function normalizeAuthResponse(data: TenantAuthResponse): TenantAuthResponse {
  const membershipRole =
    data.membershipRole ??
    data.memberships.find((m) => m.companyId === data.activeCompanyId)?.role ??
    null;

  const user: User = {
    ...data.user,
    activeCompanyId: data.activeCompanyId || null,
    membershipRole: membershipRole ?? undefined,
  };

  return {
    ...data,
    user,
    activeCompanyId: data.activeCompanyId ?? '',
    memberships: data.memberships ?? [],
    membershipRole: membershipRole ?? undefined,
  };
}

class AuthService {
  private persistAuthResponse(data: TenantAuthResponse): TenantAuthResponse {
    const normalized = normalizeAuthResponse(data);
    storeTokens(normalized.accessToken, normalized.refreshToken);
    notifyAuthSync(normalized);
    return normalized;
  }

  async login(credentials: LoginCredentials): Promise<TenantAuthResponse> {
    const response = await api.post<TenantAuthResponse>('/auth/login', credentials);
    return this.persistAuthResponse(response.data);
  }

  async register(data: RegisterData): Promise<RegisterResponse> {
    const response = await api.post<RegisterResponse>('/auth/register', data);
    const normalized = this.persistAuthResponse(response.data);
    return { ...response.data, ...normalized };
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  async refreshSession(): Promise<TenantAuthResponse> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<TenantAuthResponse>('/auth/refresh', { refreshToken });
    return this.persistAuthResponse(response.data);
  }

  async switchTenant(companyId: string): Promise<TenantAuthResponse> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<TenantAuthResponse>('/auth/switch-tenant', {
      companyId,
      refreshToken,
    });
    return this.persistAuthResponse(response.data);
  }

  async getProfile(): Promise<ProfileResponse> {
    const response = await api.get<ProfileResponse>('/auth/me');
    return response.data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/reset-password', {
      token,
      newPassword,
    });
    return response.data;
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      `/auth/verify-email/${encodeURIComponent(token)}`,
    );
    return response.data;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }
}

export default new AuthService();
